// components/PlatesInput.jsx
import {
  Autocomplete,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { useState, useMemo, useCallback } from "react";

// 1234-ABC, ABC-123 o sin guion: BD09250 (B + letra + 5 números) y BE123/BE1234.
const PLATE_REGEX = /^([A-Z]{2,4}-\d{3,4}|\d{3,4}-[A-Z]{2,4}|B[A-Z]\d{5}|BE\d{3,4})$/;

export default function PlatesInput({
  value,
  onChange,
  options = [],
  restrictToOptions = false,
  loading = false,
  onSearchInputChange,
  totalDisponible = null, // cuántas placas tiene el servicio (para avisar si no se cargaron todas)
}) {
  const [input, setInput] = useState("");

  const normalize = (s) => (s || "").trim().replace(/\s+/g, "").toUpperCase();
  const getPlate = useCallback(
    (option) => normalize(typeof option === "string" ? option : option?.placa),
    []
  );
  const getClientesText = (option) =>
    Array.isArray(option?.clientes) ? option.clientes.filter(Boolean).join(", ") : "";

  const opts = useMemo(() => {
    const seen = new Set();
    return options
      .map((option) => {
        const placa = getPlate(option);
        if (!placa || seen.has(placa)) return null;
        seen.add(placa);
        return typeof option === "string" ? { placa } : { ...option, placa };
      })
      .filter(Boolean);
  }, [getPlate, options]);
  const optsSet = useMemo(() => new Set(opts.map((option) => option.placa)), [opts]);

  const invalids = useMemo(() => value.filter((p) => !PLATE_REGEX.test(p)), [value]);

  const addMany = (items) => {
    const norm = items.map(normalize).filter(Boolean);
    const validNew = restrictToOptions ? norm.filter((p) => optsSet.has(p)) : norm;
    onChange(Array.from(new Set([...(value || []), ...validNew])));
  };

  const applyNewValue = (newValue) => {
    const normalized = newValue.map(getPlate).filter(Boolean);
    const prevSet = new Set((value || []).map(getPlate));
    const filtered = restrictToOptions
      ? normalized.filter((p) => prevSet.has(p) || optsSet.has(p))
      : normalized;
    onChange(Array.from(new Set(filtered)));
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>Placas</Typography>

      <Autocomplete
        multiple
        freeSolo={!restrictToOptions}
        options={opts}
        value={value}
        onChange={(_, newValue) => applyNewValue(newValue)}
        inputValue={input}
        onInputChange={(_, v) => {
          setInput(v);
          onSearchInputChange?.(v);
        }}
        filterSelectedOptions
        getOptionLabel={(option) => getPlate(option)}
        isOptionEqualToValue={(option, selected) => getPlate(option) === getPlate(selected)}
        renderOption={(props, option) => {
          const clientes = getClientesText(option);
          return (
            <li {...props} key={option.placa}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {option.placa}
                </Typography>
                {clientes && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {clientes}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderTags={(vals, getTagProps) =>
          vals.map((option, index) => {
            const plate = getPlate(option);
            return (
              <Chip
                {...getTagProps({ index })}
                key={plate}
                label={plate}
                size="small"
                color={PLATE_REGEX.test(plate) ? "primary" : "error"}
                variant="filled"
              />
            );
          })
        }
        renderInput={(params) => {
          const prevOnKeyDown = params.inputProps.onKeyDown;
          const prevOnPaste = params.inputProps.onPaste;

          return (
            <TextField
              {...params}
              size="small"
              placeholder={
                restrictToOptions
                  ? "Selecciona placas existentes..."
                  : "Escribe placa y presiona Enter, Coma o Espacio..."
              }
              error={invalids.length > 0}
              helperText={
                invalids.length
                  ? `Placas invalidas: ${invalids.join(", ")}`
                  : (restrictToOptions
                      ? (totalDisponible != null && totalDisponible > opts.length
                          ? `Mostrando ${opts.length} de ${totalDisponible} placas registradas: escribe parte de la placa para buscar las demás.`
                          : totalDisponible != null
                          ? `${totalDisponible} placa${totalDisponible === 1 ? "" : "s"} registrada${totalDisponible === 1 ? "" : "s"} del cliente. Solo se permiten placas registradas.`
                          : "Para mantenimiento/devolución solo se permiten placas registradas.")
                      : "Tip: puedes pegar varias separadas por coma, espacio o salto de línea.")
              }
              onKeyDown={(e) => {
                prevOnKeyDown?.(e);
                if (["Enter", ",", " "].includes(e.key)) {
                  e.preventDefault();
                  const v = normalize(input);
                  if (!v) { setInput(""); return; }

                  if (restrictToOptions) {
                    if (optsSet.has(v)) onChange(Array.from(new Set([...(value || []), v])));
                  } else {
                    onChange(Array.from(new Set([...(value || []), v])));
                  }
                  setInput("");
                }
              }}
              onPaste={(e) => {
                prevOnPaste?.(e);
                const text = e.clipboardData?.getData("text");
                if (text) {
                  e.preventDefault();
                  addMany(text.split(/[\s,;\n\r]+/g));
                  setInput("");
                }
              }}
              onBlur={() => {
                const v = normalize(input);
                if (v) {
                  if (!restrictToOptions || optsSet.has(v)) {
                    onChange(Array.from(new Set([...(value || []), v])));
                  }
                }
                setInput("");
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Total: {value.length}
          {invalids.length > 0 && (
            <> - <span style={{ color: "#d32f2f" }}>Invalidas: {invalids.length}</span></>
          )}
        </Typography>

        {!!value.length && (
          <Tooltip title="Limpiar placas">
            <IconButton size="small" onClick={() => onChange([])}>
              <BackspaceIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
